import { useTranslation } from "react-i18next";
import { Button, CardNotification } from "@foris/avocado-suite";
import { retryApprovalFlowAbilities } from "@context/useAssignmentProposalStore";
import { useProposalMode } from "@services/collections/assignment-proposal/assignmentProposalServices";
import type { ActiveProposal } from "@services/types/assignment-proposal/assignmentProposalTypes";
import { skipsIntermediateReview } from "@utils/assignmentProposal";
import { useActiveProposal } from "../../hooks/useActiveProposal";
import { useIsLinkClosedToProposals } from "../../hooks/useIsLinkClosedToProposals";
import styles from "./proposalModeBanner.module.scss";

/**
 * - "active": a new proposal or a draft; the edition requires approval.
 * - "pending": the requester's own proposal was sent and cannot be edited.
 * - "rejected": the requester corrects a rejected proposal.
 * - "reviewing": an approver reviews someone else's proposal.
 * - "closed": the link has microprogramming pending or applied.
 */
type BannerVariant = "active" | "pending" | "rejected" | "reviewing" | "closed";

const BANNER_STATE: Record<BannerVariant, "warning" | "info" | "error"> = {
  active: "warning",
  pending: "info",
  rejected: "error",
  reviewing: "info",
  closed: "warning",
};

const BANNER_KEYS = "assignation-edit.proposal.banner";

const variantOf = (
  activeProposal: ActiveProposal | null,
  isClosedToProposals: boolean,
): BannerVariant => {
  if (isClosedToProposals) return "closed";
  if (activeProposal?.mode === "review") return "reviewing";
  if (activeProposal?.mode === "rejected") return "rejected";
  if (activeProposal?.mode === "sent") return "pending";

  return "active";
};

const descriptionKeyOf = (
  variant: BannerVariant,
  activeProposal: ActiveProposal | null,
): string => {
  if (variant === "rejected" && !activeProposal?.rejectionReason) return "description-no-reason";
  if (variant === "reviewing" && !activeProposal?.requesterName) return "description-no-name";

  return "description";
};

/**
 * What the editor does with the proposal on screen, in proposal mode only; also, when the
 * approval-flow permissions could not be loaded, that nothing can be saved until a retry.
 */
const ProposalModeBanner = () => {
  const { t } = useTranslation();
  const { isProposalMode, hasAbilitiesError } = useProposalMode();
  const activeProposal = useActiveProposal();
  const isClosedToProposals = useIsLinkClosedToProposals();

  // The mode stays unknown, so nothing can be saved, until the permissions load.
  if (hasAbilitiesError) {
    return (
      <CardNotification
        className={styles.proposalModeBanner}
        state="error"
        title={t(`${BANNER_KEYS}.abilities-error.title`)}
        outlined
        extraContent={
          <Button variant="ghost" onClick={retryApprovalFlowAbilities}>
            {t(`${BANNER_KEYS}.abilities-error.retry`)}
          </Button>
        }
      >
        {t(`${BANNER_KEYS}.abilities-error.description`)}
      </CardNotification>
    );
  }

  if (!isProposalMode) return null;

  const variant = variantOf(activeProposal, isClosedToProposals);
  const variantKeys = `${BANNER_KEYS}.${variant}`;
  const description = t(`${variantKeys}.${descriptionKeyOf(variant, activeProposal)}`, {
    reason: activeProposal?.rejectionReason ?? "",
    name: activeProposal?.requesterName ?? "",
  });
  const intermediateApproverName = activeProposal?.intermediateApproverName;

  return (
    <>
      <CardNotification
        className={styles.proposalModeBanner}
        state={BANNER_STATE[variant]}
        title={t(`${variantKeys}.title`)}
        outlined
      >
        {description}
      </CardNotification>

      {skipsIntermediateReview(activeProposal) && (
        <CardNotification
          className={styles.proposalModeBanner}
          state="warning"
          title={t(`${BANNER_KEYS}.skips-intermediate.title`)}
          outlined
        >
          {intermediateApproverName
            ? t(`${BANNER_KEYS}.skips-intermediate.description`, { name: intermediateApproverName })
            : t(`${BANNER_KEYS}.skips-intermediate.description-no-name`)}
        </CardNotification>
      )}
    </>
  );
};

export default ProposalModeBanner;
